import {DataTable} from "../DataTable/DataTable";
import {
  CargoSubtype,
  compareNumber,
  containerLevelHistoryTypes,
  ContainerStatusEntity,
  inchesToFeet
} from "@ots-frontend/ots-data";
import {Anchor, Group} from "@mantine/core";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Progress} from "../../progress/Progress/Progress";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useContainerStatusDataTableFilter} from "./useContainerStatusDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {DataTableColumn} from "mantine-datatable";
import {useMemo} from "react";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {ContainerStatusIcon} from "../../icon/ContainerStatusIcon/ContainerStatusIcon";

const EMPTY_ARRAY: ContainerStatusEntity[] = []

export type ContainerStatusDataTableProps = EntityDataTableProps<ContainerStatusEntity> & {
  getHeightValue?: (status: ContainerStatusEntity) => number | undefined,
  columns?: {
    route?: boolean
    site?: boolean
    reject?: boolean
    level?: boolean
    primaryLevel?: CargoSubtype
    secondaryLevel?: CargoSubtype
    fill?: boolean
  }
}

export const ContainerStatusDataTable = (props: ContainerStatusDataTableProps) => {
  const {
    records: statuses = EMPTY_ARRAY,
    recordsPerPageOptions,
    getHeightValue = (status) => status.meterFill?.actualHeight,
    columns: columnsConfig,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useContainerStatusDataTableFilter({
    records: statuses
  })

  const sortConfig = useMemo(() => {
    return{
      "level": {
        sort: (a, b) => {
          const aLevel = getHeightValue(a)
          const bLevel = getHeightValue(b)
          return compareNumber(aLevel, bLevel)
        }
      },
      "primaryLevel": {
        sort: (a, b) => {
          const cargoSubtype = columnsConfig?.primaryLevel
          const aLevel = cargoSubtype ? a.getFill(cargoSubtype, containerLevelHistoryTypes.meter)?.actualHeight : undefined
          const bLevel = cargoSubtype ? b.getFill(cargoSubtype, containerLevelHistoryTypes.meter)?.actualHeight : undefined
          return compareNumber(aLevel, bLevel)
        },
      },
      "secondaryLevel": {
        sort: (a, b) => {
          const cargoSubtype = columnsConfig?.secondaryLevel
          const aLevel = cargoSubtype ? a.getFill(cargoSubtype, containerLevelHistoryTypes.meter)?.actualHeight : undefined
          const bLevel = cargoSubtype ? b.getFill(cargoSubtype, containerLevelHistoryTypes.meter)?.actualHeight : undefined
          return compareNumber(aLevel, bLevel)
        }
      },
    }
  }, [columnsConfig?.primaryLevel, columnsConfig?.secondaryLevel, getHeightValue])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortConfig: sortConfig,
    sortStatus: {
      columnAccessor: 'containerName', direction: 'asc',
    }
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns: DataTableColumn<ContainerStatusEntity>[] = useMemo(() => {
    const _columns: DataTableColumn<ContainerStatusEntity>[] = []

    _columns.push({
      ...getColumnFilterProps(filterConfig, "name"),
      title: t('entity.containerstatus.table.column.name', 'Name'),
      accessor: 'containerName',
      width: 100,
      sortable: true,
      render: ({containerId, containerName}) => (
        <Anchor component={Link} to={`/containers/profile?id=${containerId}`}>
          {containerName}
        </Anchor>
      ),
    })

    if (columnsConfig?.site) {
      _columns.push({
        ...getColumnFilterProps(filterConfig, "site"),
        title: t('entity.containerstatus.table.column.site', 'Site'),
        accessor: 'siteName',
        width: 100,
        sortable: true,
        render: ({siteId, siteName}) => (
          siteId ? <EntityAnchor value={{objectId: siteId, objectType: "SITE", objectName: siteName}}/> : undefined
        ),
      })
    }

    if (columnsConfig?.route) {
      _columns.push({
        ...getColumnFilterProps(filterConfig, "route"),
        title: t('entity.containerstatus.table.column.route', 'Route'),
        accessor: 'siteRouteName',
        width: 100,
        sortable: true,
        render: ({siteRouteId, siteRouteName}) => (
          siteRouteId ?
            <EntityAnchor value={{objectId: siteRouteId, objectType: "ROUTE", objectName: siteRouteName}}/> : undefined
        ),
      })
    }

    if (columnsConfig?.level) {
      _columns.push({
        title: t('entity.containerstatus.table.column.level', 'Level'),
        accessor: 'meterFill.actualHeight',
        width: 100,
        sortable: true,
        render: (containerStatus) => {
          const height = getHeightValue(containerStatus)
          if (typeof height !== 'undefined') {
            const feetInches = inchesToFeet(height);
            return `${feetInches.feet.toFixed(0)}' ${feetInches.inches.toFixed(
              0
            )}" ${feetInches.quarters.toFixed(0)}/4`;
          }

          return undefined
        }
      })
    }

    if (columnsConfig?.primaryLevel) {
      const cargoSubtype = columnsConfig.primaryLevel
      _columns.push({
        title: t(
          'entity.containerstatus.table.column.cargosubtypelevel',
          '{{cargoSubtype}} Level', {
            cargoSubtype: t(cargoSubtype.displayValue.id, cargoSubtype.displayValue.defaultMessage)
          }
        ),
        accessor: 'primaryLevel',
        width: 100,
        sortable: true,
        render: (containerStatus) => {
          const height = containerStatus.getFill(cargoSubtype, containerLevelHistoryTypes.meter)?.actualHeight
          if (typeof height !== 'undefined') {
            const feetInches = inchesToFeet(height);
            return `${feetInches.feet.toFixed(0)}' ${feetInches.inches.toFixed(
              0
            )}" ${feetInches.quarters.toFixed(0)}/4`;
          }

          return undefined
        }
      })
    }

    if (columnsConfig?.secondaryLevel) {
      const cargoSubtype = columnsConfig.secondaryLevel
      _columns.push({
        title: t(
          'entity.containerstatus.table.column.cargosubtypelevel',
          '{{cargoSubtype}} Level', {
            cargoSubtype: t(cargoSubtype.displayValue.id, cargoSubtype.displayValue.defaultMessage)
          }
        ),
        accessor: 'secondaryLevel',
        width: 100,
        sortable: true,
        customCellAttributes: (record) => {
          const attrs: Record<string, unknown> = {}

          if (record.meterFill?.exceedsMaxNonPrimaryVolume) {
            attrs["bg"] = "red.3"
          }

          return attrs
        },
        render: (containerStatus) => {
          const height = containerStatus.getFill(cargoSubtype, containerLevelHistoryTypes.meter)?.actualHeight
          if (typeof height !== 'undefined') {
            const feetInches = inchesToFeet(height);
            return `${feetInches.feet.toFixed(0)}' ${feetInches.inches.toFixed(
              0
            )}" ${feetInches.quarters.toFixed(0)}/4`;
          }

          return undefined
        }
      })
    }

    if (columnsConfig?.fill) {
      _columns.push({
        title: t('entity.containerstatus.table.column.fill', 'Fill'),
        accessor: "meterFill.actualPercent",
        width: 100,
        sortable: true,
        textAlignment: 'right',
        render: (containerStatus) => {
          const fill = containerStatus?.meterFill
          const percent = fill?.actualPercent
          const status = fill?.actualStatus
          return <Progress color={status === "CRITICAL" ? "red" : undefined} value={(percent ?? 0) * 100}/>
        }
      })
    }

    if (columnsConfig?.reject) {
      _columns.push({
        title: t('entity.containerstatus.table.column.reject', 'Rejection'),
        accessor: "inRejectionRestriction",
        width: 100,
        sortable: true,
        textAlignment: 'right',
        render: (record) => (
          <Group spacing={4} position="right" noWrap>
            {record.inRejectionRestriction && (
              <ContainerStatusIcon name="rejection-restriction" width={16} height={16}/>
            )}
          </Group>
        )
      })
    }

    return _columns
  }, [filterConfig, columnsConfig, getHeightValue, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}
